import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useWorkspace } from '../contexts/WorkspaceContext';
import TeamOnboardingFlow from './TeamOnboardingFlow';
import InviteTeamModal from './InviteTeamModal';

const SimpleWorkspaceDropdown = () => {
  const { 
    currentWorkspace, 
    workspaces, 
    switchWorkspace,
    createOrganization,
    inviteToOrganization,
    isOrganizationWorkspace 
  } = useWorkspace();
  
  const [isOpen, setIsOpen] = useState(false);
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const handleOnboardingComplete = async (onboardingData) => {
    // Create organization with data from onboarding flow
    const orgData = {
      name: onboardingData.organizationName,
      useCase: onboardingData.useCase,
      teamType: onboardingData.teamType,
      role: onboardingData.role,
      templates: onboardingData.templates,
      plan: onboardingData.plan
    };
    
    await createOrganization(orgData);
    setShowOnboardingFlow(false);
    setIsOpen(false);
  };

  const handleInviteTeam = async (invites) => {
    for (const invite of invites) {
      await inviteToOrganization(invite.email, invite.role);
    }
    setShowInviteModal(false);
    setIsOpen(false);
  };

  const displayName = currentWorkspace?.name === 'Personal' ? 'Personal' : currentWorkspace?.name || 'Personal';
  const isPersonal = currentWorkspace?.type === 'personal';

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 w-full px-2 py-1 text-xs font-medium text-gray-500 dark:text-[#666666] hover:text-brand transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-[#1A1A1A]"
        >
          <span className="truncate">{displayName}</span>
          <ChevronDownIcon className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsOpen(false)}
              />

              {/* Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg shadow-lg z-40 overflow-hidden"
              >
                {/* Personal Workspace */}
                <button
                  onClick={() => {
                    if (!isPersonal) {
                      const personalWorkspace = { 
                        id: `personal_${currentWorkspace?.ownerId}`, 
                        name: 'Personal', 
                        type: 'personal' 
                      };
                      switchWorkspace(personalWorkspace);
                    }
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors ${
                    isPersonal ? 'bg-gray-50 dark:bg-[#1A1A1A]' : ''
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center">
                    <span className="text-xs font-bold text-black">P</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Personal
                    </div>
                  </div>
                  {isPersonal && (
                    <div className="w-2 h-2 bg-brand rounded-full"></div>
                  )}
                </button>

                {/* Organization Workspaces */}
                {workspaces.map((workspace) => (
                  <button
                    key={workspace.id}
                    onClick={() => {
                      switchWorkspace(workspace);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors ${
                      currentWorkspace?.id === workspace.id ? 'bg-gray-50 dark:bg-[#1A1A1A]' : ''
                    }`}
                  >
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#E6A15E] to-brand flex items-center justify-center">
                      <UserGroupIcon className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {workspace.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-[#666666]">
                        {workspace.members?.length || 1} member{workspace.members?.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {currentWorkspace?.id === workspace.id && (
                      <div className="w-2 h-2 bg-brand rounded-full"></div>
                    )}
                  </button>
                ))}

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-[#2A2A2A]">
                  {/* Create Organization */}
                  <button
                    onClick={() => {
                      setShowOnboardingFlow(true);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <div className="w-6 h-6 rounded-md border border-dashed border-gray-300 dark:border-[#2A2A2A] flex items-center justify-center">
                      <PlusIcon className="w-3 h-3 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Create Team
                      </div>
                    </div>
                  </button>

                  {/* Invite Members (only show if in organization) */}
                  {isOrganizationWorkspace && (
                    <button
                      onClick={() => {
                        setShowInviteModal(true);
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors"
                    >
                      <div className="w-6 h-6 rounded-md bg-brand/10 flex items-center justify-center">
                        <UserGroupIcon className="w-3 h-3 text-brand" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Invite Members
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <TeamOnboardingFlow
        isOpen={showOnboardingFlow}
        onClose={() => setShowOnboardingFlow(false)}
        onComplete={handleOnboardingComplete}
      />

      <InviteTeamModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteTeam}
        organizationName={currentWorkspace?.name}
      />
    </>
  );
};

export default SimpleWorkspaceDropdown;